import FontFaceObserver from 'fontfaceobserver';
import scriptjs from 'scriptjs';

function LoadError(resourceType, resource, data) {
    this.resourceType = resourceType;
    this.resource = resource;
    this.data = data;
}

const loadingTools = {
    injectStyle(css) {
        if (typeof document === 'undefined') {
            return undefined;
        }
        const head = document.head || document.getElementsByTagName('head')[0];
        const style = document.createElement('style');
        style.resourceType = 'text/css';
        if (style.styleSheet) {
            style.styleSheet.cssText = css;
        } else {
            style.appendChild(document.createTextNode(css));
        }
        head.appendChild(style);

        return style;
    },

    loadFont(familyName) {
        const font = new FontFaceObserver(familyName);

        return font.load()
            .catch((error) => {
                throw new LoadError('font', `Font family: ${familyName}`, error);
            });
    },

    loadFonts(fontFamilyNames) {
        const fontPromises = fontFamilyNames.map(familyName => this.loadFont(familyName));
        return Promise.all(fontPromises);
    },

    loadImage(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.addEventListener('error', (event) => {
                reject({ resourceType: 'image', url, data: event });
            });
            img.addEventListener('load', () => {
                resolve(url);
            });
            img.src = url;
        });
    },

    loadCss(url) {
        return new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.href = url;
            link.resourceType = 'text/css';
            link.rel = 'stylesheet';

            function checkIfReady() {
                const sheet = link.sheet || link.styleSheet;
                return !!sheet;
            }

            function repeatedlyCheckIfReady(remainingMs) {
                if (checkIfReady()) {
                    resolve(url);
                    return;
                }
                if (remainingMs <= 0) {
                    reject(url);
                    return;
                }

                setTimeout(() => {
                    repeatedlyCheckIfReady(remainingMs - 20);
                }, 20);
            }

            if ('onload' in link) {
                link.addEventListener('error', (event) => {
                    reject({ resourceType: 'css', url, data: event });
                });
                link.addEventListener('load', () => {
                    resolve(url);
                });
            } else {
                repeatedlyCheckIfReady(10000);
            }

            document.getElementsByTagName('head')[0].appendChild(link);
        });
    },

    async loadLibrary(url, expectedGlobal) {
        return new Promise((resolve, reject) => {
            scriptjs(url, () => {
                if (!window[expectedGlobal] && expectedGlobal !== undefined) {
                    scriptjs.get(url, () => {
                        if (expectedGlobal !== undefined && !window[expectedGlobal]) {
                            reject(`Could not load ${url} on second attempt. window[${expectedGlobal}] not found.`);
                        }
                        resolve(window[expectedGlobal]);
                    });
                }
                resolve(window[expectedGlobal]);
            });
        });
    },

    async loadLibraries(libraries) {
        const promisedLibraries = libraries.map((library) => {
            const { expectedGlobal, url } = library;
            return loadingTools.loadLibrary(url, expectedGlobal);
        });

        return Promise
            .all(promisedLibraries)
            .catch(error => window.console.error(error));
    },
};

export default loadingTools;
